// Decides whether the shared SPA build should render the Data API portal or the marketing site for
// the current hostname.
//
// The canonical portal host(s) are configurable via env (VITE_DATAAPI_HOSTS), defaulting to
// dataapi.reachthepeople.ai. Reseller white-label domains are NOT hardcoded — an unknown host is
// resolved at runtime by probing the public branding endpoint (see usePortalHostDecision), so
// onboarding a reseller needs only a reseller record + DNS, never a frontend redeploy.

export const PORTAL_HOST = "portal";
export const MARKETING_HOST = "marketing";
// Known marketing/portal hosts resolve synchronously; an UNRESOLVED_HOST must be probed.
export const UNRESOLVED_HOST = "unresolved";

const DEFAULT_DATAAPI_HOST = "dataapi.reachthepeople.ai";

const parseHosts = (raw) =>
  (raw || "")
    .split(",")
    .map((host) => host.trim().toLowerCase())
    .filter(Boolean);

// Canonical portal hosts: VITE_DATAAPI_HOSTS (comma-separated) replaces the built-in default when
// set. VITE_DATAAPI_HOST is the legacy single-host var and is still honored.
const configuredPortalHosts = parseHosts(import.meta.env.VITE_DATAAPI_HOSTS);
const legacyPortalHost = (import.meta.env.VITE_DATAAPI_HOST || "")
  .trim()
  .toLowerCase();

const PORTAL_HOSTS = new Set(
  configuredPortalHosts.length ? configuredPortalHosts : [DEFAULT_DATAAPI_HOST]
);
if (legacyPortalHost) {
  PORTAL_HOSTS.add(legacyPortalHost);
}

// Unambiguous marketing hosts (incl. the donation subdomain, which the marketing tree renders
// itself). Listed so they skip the branding probe and never flash a loader. Extra hosts can be
// added via VITE_MARKETING_HOSTS without touching code.
const MARKETING_HOSTS = new Set([
  "reachthepeople.ai",
  "www.reachthepeople.ai",
  "raise.reachthepeople.ai",
  "localhost",
  "127.0.0.1",
  ...parseHosts(import.meta.env.VITE_MARKETING_HOSTS),
]);

// Synchronous first pass. Returns PORTAL_HOST / MARKETING_HOST for known hosts (and the ?dataapi
// opt-in), or UNRESOLVED_HOST when the host must be probed against the branding endpoint.
export const resolvePortalHostSync = () => {
  let hostname = "";
  try {
    hostname = window.location.hostname.toLowerCase();
  } catch {
    return MARKETING_HOST;
  }

  // Local/dev opt-in: ?dataapi=1 forces the portal and persists for the session; ?dataapi=0 exits.
  try {
    const flag = new URLSearchParams(window.location.search).get("dataapi");
    if (flag === "1") {
      window.sessionStorage.setItem("dataapi", "1");
      return PORTAL_HOST;
    }
    if (flag === "0") {
      window.sessionStorage.removeItem("dataapi");
      return MARKETING_HOST;
    }
    if (window.sessionStorage.getItem("dataapi") === "1") {
      return PORTAL_HOST;
    }
  } catch {
    // sessionStorage unavailable — fall through to host matching.
  }

  if (PORTAL_HOSTS.has(hostname)) {
    return PORTAL_HOST;
  }
  if (MARKETING_HOSTS.has(hostname)) {
    return MARKETING_HOST;
  }
  return UNRESOLVED_HOST;
};
